import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, MapPin } from "lucide-react";
import { allEvents, evUI } from "../data/localizeEvents";
import { useLang } from "../i18n/LanguageContext";
import { useLocalizedPath } from "../i18n/langPath";

export default function Events() {
  const { lang } = useLang();
  const lp = useLocalizedPath();
  const ui = evUI(lang);
  const events = allEvents(lang).slice(0, 6);

  return (
    <section id="events" className="bg-slate-50 py-20 md:py-28" data-testid="events-section">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div className="max-w-2xl">
            <span className="text-xs uppercase tracking-[0.25em] text-[#F5B400] font-semibold">{ui.section_eyebrow}</span>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-bold text-black leading-tight">{ui.section_title}</h2>
            <p className="mt-4 text-slate-500 text-lg">{ui.section_sub}</p>
          </div>
          <Link to={lp("/events")} className="hidden md:inline-flex items-center gap-2 text-sm font-semibold text-[#F5B400] hover:gap-3 transition-all whitespace-nowrap" data-testid="events-view-all">
            {ui.cta_all} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {events.map((e, i) => (
            <Link key={e.slug} to={lp(`/events/${e.slug}`)} data-testid={`event-card-${e.slug}`}
              className="group relative rounded-2xl overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-300 bg-white">
              <div className="relative h-60 overflow-hidden">
                <img src={e.image} alt={e.name} loading={i > 1 ? "lazy" : "eager"} decoding="async" width="400" height="240"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                <span className="absolute top-4 left-4 text-[11px] uppercase tracking-wider font-semibold text-white bg-[#F5B400] rounded-full px-3 py-1">{e.category}</span>
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <h3 className="font-display text-xl font-bold text-white">{e.name}</h3>
                  <div className="mt-1 flex items-center gap-4 text-xs text-white/80">
                    <span className="flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" /> {e.period}</span>
                  </div>
                </div>
              </div>
              <div className="p-5">
                <p className="text-slate-600 text-sm leading-relaxed">{e.teaser}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#F5B400] group-hover:gap-2.5 transition-all">
                  {ui.book_cta} <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-8 md:hidden">
          <Link to={lp("/events")} className="inline-flex items-center gap-2 text-sm font-semibold text-[#F5B400]">
            {ui.cta_all} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
